/**
 * Practice-mode card for the einbuergerungstest-vokabeln app.
 *
 * Shows one German word and three English options — one correct, two drawn
 * from the word's curated distractor pool. Auxiliary affordances:
 *   - "I don't know" under the options: records an incorrect answer, same as
 *     the sibling apps.
 *   - Bookmark icon (header): toggles the persistent bookmark.
 *   - "Already know it" icon (header): marks the card memorized and skips it.
 *     The extraction filter cannot know which words the user already has, so
 *     it keeps borderline ones and makes them cheap to remove.
 *
 * Why three options and not four: the exam itself uses four, but this app is
 * not simulating the exam — it is drilling recognition of a single word. Three
 * keeps the card scannable on a phone without stacking six lines of English.
 *
 * ## Why the two escape hatches are far apart
 *
 * The first version shipped with only "I already know this", as a text link
 * under the options — which is exactly where Der/Die/Das and Einbürgerungstest
 * put "I don't know". Muscle memory from those apps therefore hit a button
 * that meant the opposite, and one tap silently memorized a word and dropped
 * it from rotation.
 *
 * So: "I don't know" now occupies that position and behaves like its siblings,
 * and the destructive one moved into the header as an icon. Two actions with
 * opposite meanings must never share a location across apps.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Bookmark, CircleCheck } from 'lucide-react';

import type { VokabelnPayload } from '@/core/types';
import { api } from '@/lib/api';
import { seededSample, seededShuffle } from '@/lib/shuffle';
import { cn } from '@/lib/utils';

import type { CardFrontProps } from '../types';
import { correctGloss, genderBorder, genderText, isAnswerCorrect } from './card-utils';

/** How many wrong options accompany the correct one. */
const DISTRACTORS_SHOWN = 2;

export function CardFront({ card, onAnswer }: CardFrontProps<VokabelnPayload>) {
  const startRef = useRef<number>(Date.now());
  const [bookmarked, setBookmarked] = useState<boolean>(card.bookmarked ?? false);
  const [bookmarkPending, setBookmarkPending] = useState(false);
  const [knownPending, setKnownPending] = useState(false);

  useEffect(() => {
    startRef.current = Date.now();
    setBookmarked(card.bookmarked ?? false);
  }, [card.id, card.bookmarked]);

  const answer = correctGloss(card);

  // Draw two distractors from the pool and shuffle the correct answer in among
  // them. Both steps are seeded on the card id, so the options are stable while
  // the user reads but differ card-to-card — and, because the pool is larger
  // than what is shown, differ between passes over the same word too.
  const options = useMemo(() => {
    const wrong = seededSample(card.payload.distractors, DISTRACTORS_SHOWN, card.id);
    return seededShuffle([answer, ...wrong], card.id + 1);
  }, [card.id, card.payload.distractors, answer]);

  const submit = (chosen: string | null) => {
    onAnswer({
      // null is "I don't know" — never correct, same as the sibling apps.
      correct: isAnswerCorrect(chosen, answer),
      user_answer: chosen,
      time_ms: Date.now() - startRef.current,
    });
  };

  const toggleBookmark = async () => {
    if (bookmarkPending) return;
    const next = !bookmarked;
    setBookmarked(next);
    setBookmarkPending(true);
    try {
      await api.toggleBookmark(card.id, next);
    } catch {
      // Roll back on failure; the icon reverting is the signal.
      setBookmarked(!next);
    } finally {
      setBookmarkPending(false);
    }
  };

  /**
   * "Already know it" — hand the assertion to the shell.
   *
   * The persistence deliberately lives in PracticeRoute rather than here: the
   * shell owns session-scoped writes (attempts, finish), and marking a card
   * known has to adjust the session's card total so the skipped card doesn't
   * drag the accuracy down. The card front only reports what the user did.
   */
  const markKnown = () => {
    if (knownPending) return;
    setKnownPending(true);
    onAnswer({
      correct: true,
      user_answer: answer,
      time_ms: Date.now() - startRef.current,
      kind: 'asserted_known',
    });
  };

  return (
    <div
      className={cn(
        'border-border bg-card relative flex w-full flex-col gap-8 rounded-2xl border p-6 shadow-sm sm:p-8',
        'border-l-4',
        genderBorder(card.payload.article),
      )}
    >
      {/* Header affordances. "Already know it" lives up here, deliberately far
          from the answer buttons — see the note at the top of this file. */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        <button
          type="button"
          aria-label="I already know this word — mark it learned and skip it"
          title="I already know this word"
          onClick={markKnown}
          disabled={knownPending}
          className={cn(
            'text-muted-foreground flex h-9 w-9 items-center justify-center rounded-md transition-colors',
            'hover:bg-muted focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
            'disabled:opacity-40',
          )}
        >
          <CircleCheck className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label={bookmarked ? 'Remove bookmark' : 'Bookmark this word'}
          onClick={toggleBookmark}
          className={cn(
            'text-muted-foreground flex h-9 w-9 items-center justify-center rounded-md transition-colors',
            'hover:bg-muted focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
            bookmarked && 'text-primary',
          )}
        >
          <Bookmark className={cn('h-5 w-5', bookmarked && 'fill-current')} />
        </button>
      </div>

      {/* The word. Nouns carry their article so gender is learned for free —
          this app doesn't test gender (derdiedas does), so giving it away here
          costs nothing. */}
      <div className="flex flex-col items-center gap-1 pt-6 text-center">
        <div className="font-display text-4xl font-semibold sm:text-5xl">
          {card.payload.article ? (
            <>
              <span className={genderText(card.payload.article)}>{card.payload.article} </span>
              {card.de}
            </>
          ) : (
            card.de
          )}
        </div>
        {card.payload.pos !== 'noun' && (
          <div className="text-muted-foreground text-sm">{card.payload.pos}</div>
        )}
      </div>

      <div className="flex w-full flex-col gap-3">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => submit(option)}
            className={cn(
              'border-border bg-card min-h-14 w-full rounded-lg border-2 px-4 py-3 text-base transition-colors',
              'hover:border-primary hover:text-primary',
              'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
            )}
          >
            {option}
          </button>
        ))}
      </div>

      {/* Same position and same meaning as in Der/Die/Das and
          Einbürgerungstest: an honest miss, recorded as incorrect. */}
      <button
        type="button"
        onClick={() => submit(null)}
        className="text-muted-foreground mx-auto text-sm underline-offset-4 hover:underline"
      >
        I don't know
      </button>
    </div>
  );
}
